/**
 * ESERV2-1449 — once a Heritage order is placed the Send page disables Submit
 * and shows where the order is ("Submitted", "Processing", "Shipped"…).
 *
 * The facts come from the send-list row (getSend): how many branch groups the
 * PO needs, how many have a successful Heritage order, and the latest status
 * Heritage reported (kept current by the status webhook). "Submitted" is
 * judged per branch group because a split PO (#37) whose second branch failed
 * must keep Submit enabled so the retry can finish it.
 */

export interface HeritageSubmissionFields {
	is_heritage?: boolean | null;
	heritage_submitted_groups?: number | string | null;
	heritage_required_groups?: number | string | null;
	heritage_status_code?: string | number | null;
	heritage_status_description?: string | null;
}

export type HeritageSubmissionLabel =
	| "Submitted"
	| "Processing"
	| "Partially shipped"
	| "Shipped"
	| "On hold"
	| "Cancelled"
	| "Complete";

export interface HeritageSubmissionState {
	/** Every branch group has its Heritage order — Submit is disabled. */
	submitted: boolean;
	/** Some groups placed, some not — Submit stays enabled for the retry. */
	partial: boolean;
	/** Translation key for the status chip; null when nothing was placed. */
	label: HeritageSubmissionLabel | "Partially submitted" | null;
	/** Secondary text: Heritage's own description, or "1/2 branches" for a partial. */
	detail: string | null;
}

const NONE: HeritageSubmissionState = {
	submitted: false,
	partial: false,
	label: null,
	detail: null,
};

/** Heritage status codes (design doc §9.4) folded into the few words the page shows. */
export function heritageSubmissionLabel(
	code: string | number | null | undefined
): HeritageSubmissionLabel | null {
	if (code == null || code === "") return null;
	const normalized = String(code).trim();
	const padded = /^\d$/.test(normalized) ? `0${normalized}` : normalized;
	switch (padded) {
		case "00":
			return "Submitted";
		case "10":
		case "20":
		case "30":
			return "Processing";
		case "40":
			return "Partially shipped";
		case "50":
			return "Shipped";
		case "17":
			return "On hold";
		case "99":
			return "Cancelled";
		case "100":
		case "101":
			return "Complete";
		default:
			return null;
	}
}

const toCount = (value: number | string | null | undefined): number => {
	if (value == null || value === "") return 0;
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export function resolveHeritageSubmission(
	po: HeritageSubmissionFields | null | undefined
): HeritageSubmissionState {
	if (!po || !po.is_heritage) return NONE;

	const submittedGroups = toCount(po.heritage_submitted_groups);
	if (submittedGroups === 0) return NONE;

	// A PO always needs at least one order, whatever the line data says.
	const requiredGroups = Math.max(toCount(po.heritage_required_groups), 1);

	if (submittedGroups < requiredGroups) {
		return {
			submitted: false,
			partial: true,
			label: "Partially submitted",
			detail: `${submittedGroups}/${requiredGroups}`,
		};
	}

	return {
		submitted: true,
		partial: false,
		label: heritageSubmissionLabel(po.heritage_status_code) ?? "Submitted",
		detail: po.heritage_status_description ?? null,
	};
}
